import { createContext, useContext, useEffect, useState } from "react";
import { useLDClient } from "launchdarkly-react-client-sdk";
import {
  ALL_NAV_HIDE_FLAGS,
  DEFAULT_HIDDEN_FLAGS,
  HIDE_INFO_ASSISTANT_FLAG,
  HIDE_VIBE_CODING_STUDIO_FLAG,
  isNavItemVisibleByFlags,
} from "@/lib/navFeatureFlags";
import { readHiddenFlagsFromClient } from "@/contexts/featureFlagHelpers";
import {
  defaultWorkflowEnableFlags,
  fetchWorkflowFlagsFromMaster,
  isWorkflowAgentEnabled as isWorkflowAgentEnabledForFlags,
  mergeWorkflowFlagsFromApi,
  readWorkflowEnableFlagsFromClient,
} from "@/lib/workflowFeatureFlags";

export { HIDE_VIBE_CODING_STUDIO_FLAG, HIDE_INFO_ASSISTANT_FLAG } from "@/lib/navFeatureFlags";

const defaultValue = {
  hiddenFlags: DEFAULT_HIDDEN_FLAGS,
  workflowFlags: defaultWorkflowEnableFlags(),
};

const FeatureFlagContext = createContext(defaultValue);

export function FeatureFlagBridgeStatic({ children }) {
  return (
    <FeatureFlagContext.Provider value={defaultValue}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function FeatureFlagBridgeWithLd({ children }) {
  const ldClient = useLDClient();
  const [hiddenFlags, setHiddenFlags] = useState(DEFAULT_HIDDEN_FLAGS);
  const [workflowFlags, setWorkflowFlags] = useState(defaultWorkflowEnableFlags());
  const [apiWorkflowPayload, setApiWorkflowPayload] = useState(null);

  useEffect(() => {
    let cancelled = false;
    void fetchWorkflowFlagsFromMaster().then((payload) => {
      if (cancelled || !payload) {
        return;
      }
      setApiWorkflowPayload(payload);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ldClient) {
      return;
    }

    const sync = () => {
      const nextHidden = readHiddenFlagsFromClient(ldClient);
      setHiddenFlags(nextHidden);
      const fromLd = readWorkflowEnableFlagsFromClient(ldClient);
      setWorkflowFlags(mergeWorkflowFlagsFromApi(fromLd, apiWorkflowPayload));
      if (process.env.NODE_ENV === "development") {
        const active = ALL_NAV_HIDE_FLAGS.filter((flag) => nextHidden[flag]);
        console.info("[LaunchDarkly] hidden nav flags:", active.length ? active : "(none)");
      }
    };

    void ldClient.waitUntilReady().then(sync);
    ldClient.on("ready", sync);
    ldClient.on("change", sync);

    return () => {
      ldClient.off("ready", sync);
      ldClient.off("change", sync);
    };
  }, [ldClient, apiWorkflowPayload]);

  return (
    <FeatureFlagContext.Provider value={{ hiddenFlags, workflowFlags }}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function useHiddenFlags() {
  return useContext(FeatureFlagContext).hiddenFlags;
}

/** Visible when the hide flag is OFF (default). */
export function useIsNavItemVisibleByFlag(featureFlag) {
  const hiddenFlags = useHiddenFlags();
  return isNavItemVisibleByFlags(featureFlag, hiddenFlags);
}

/** Visible when hide-vibe-coding-studio is OFF (default). */
export function useVibeCodingStudioVisible() {
  return useIsNavItemVisibleByFlag(HIDE_VIBE_CODING_STUDIO_FLAG);
}

/** Visible when hide-info-assistant is OFF (default). Controls the floating assistant widget. */
export function useIsInfoAssistantVisible() {
  return useIsNavItemVisibleByFlag(HIDE_INFO_ASSISTANT_FLAG);
}

export function useWorkflowFlags() {
  return useContext(FeatureFlagContext).workflowFlags;
}

/** True when master + per-agent LaunchDarkly workflow flags allow this agent. */
export function useWorkflowAgentEnabled(agentId) {
  const workflowFlags = useWorkflowFlags();
  return isWorkflowAgentEnabledForFlags(agentId, workflowFlags);
}
